import React, { FC, ReactNode } from "react";

import { SxProps } from "@mui/material";

import { StyledDivider } from "@components/components";

import * as Styled from "./styles";
import { setBackgroundImageStyle } from "./utils";

type TPageContainer = {
  isPage?: boolean;
  backgroundImage?: string;
  children: ReactNode;
  withBanner?: boolean;
  withDivider?: boolean;
  isCentered?: boolean;
  isHero?: boolean;
  id?: string;
  minHeight?: number;
  isBackgroundPositionBottom?: boolean;
  noGradientOverlay?: boolean;
  /** Adds a gradient fade-to-brown overlay at the bottom of the section so
      tall sections (expanded FAQs, long card grids) transition smoothly
      into the container's solid-brown background instead of showing a hard
      edge where the background image caps at 100vh. Opt-in — most sections
      are short enough that the image covers everything. */
  withBottomFade?: boolean;
  style?: React.CSSProperties;
  sx?: SxProps;
};

export const PageContainer: FC<TPageContainer> = ({
  isPage = false,
  backgroundImage = "",
  children,
  withBanner = false,
  withDivider = false,
  isCentered = false,
  isHero = false,
  minHeight = 0,
  isBackgroundPositionBottom = false,
  noGradientOverlay = false,
  withBottomFade = false,
  id,
  style,
  sx,
}) => {
  const backgroundImageStyle = setBackgroundImageStyle(noGradientOverlay ? false : isPage, backgroundImage);

  // Separate background-related styles so they apply to the BackgroundLayer, not the container.
  // `maxHeight` is included so callers can override the default 100vh cap —
  // tall sections with `maxHeight: "none"` get the background image
  // stretched across the full section height (useful when the 100vh cap
  // would otherwise expose the container's solid brown below).
  const bgKeys = ["backgroundImage", "backgroundSize", "backgroundPosition", "backgroundPositionX", "backgroundPositionY", "backgroundRepeat", "backgroundAttachment", "maskImage", "WebkitMaskImage", "maskSize", "WebkitMaskSize", "maskPosition", "WebkitMaskPosition", "maskRepeat", "WebkitMaskRepeat", "filter", "WebkitFilter", "maxHeight", "height"] as const;
  const bgStyleOverrides: React.CSSProperties = {};
  const containerStyle: React.CSSProperties = {};
  if (style) {
    for (const [key, value] of Object.entries(style)) {
      if (bgKeys.includes(key as any)) {
        (bgStyleOverrides as any)[key] = value;
      } else {
        (containerStyle as any)[key] = value;
      }
    }
  }

  return (
    <Styled.Container
      id={id}
      $isPage={isPage}
      $withBanner={withBanner}
      $isHero={isHero}
      $minHeight={minHeight}
      $isBackgroundPositionBottom={isBackgroundPositionBottom}
      style={Object.keys(containerStyle).length ? containerStyle : undefined}
      sx={sx}
    >
      {backgroundImage && (
        <Styled.BackgroundLayer
          $isPage={isPage}
          $isBackgroundPositionBottom={isBackgroundPositionBottom}
          style={{ ...backgroundImageStyle, ...bgStyleOverrides }}
        />
      )}
      {withBottomFade && <Styled.BottomFade />}
      <Styled.ContentContainer $isCentered={isCentered} $isHero={isHero}>
        {children}
      </Styled.ContentContainer>
      {withDivider && <StyledDivider $isPage={isPage} $isSection />}
    </Styled.Container>
  );
};
